import type { AjusteSaldoInput, ClienteCrearInput, ClienteCreditoInput, ClienteDTO, ClienteEditarBasicoInput, EstadoCuentaDTO, NotaCreditoSaldoDTO, OperacionSaldoDTO, TraspasoSaldoInput } from '@mg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, get, patch, post } from '@/lib/api.ts';

export interface FiltrosClientes {
  q?: string | undefined;
  tipo?: 'ordinario' | 'especial' | undefined;
  condicion?: 'contado' | 'credito' | undefined;
  activo?: 'true' | 'false' | 'todos' | undefined;
  pagina?: number | undefined;
  limite?: number | undefined;
}
export interface PaginaClientes {
  datos: ClienteDTO[];
  total: number;
  pagina: number;
  limite: number;
}

export const clavesClientes = {
  lista: (f: FiltrosClientes) => ['clientes', 'lista', f] as const,
  estado: (id: string) => ['clientes', 'estado', id] as const,
};

export const useClientes = (f: FiltrosClientes, habilitado = true) =>
  useQuery({ queryKey: clavesClientes.lista(f), queryFn: () => get<PaginaClientes>('/clientes', f as Record<string, string | number | undefined>), placeholderData: (p) => p, enabled: habilitado });
export const useEstadoCuenta = (id: string | null) => useQuery({ queryKey: clavesClientes.estado(id ?? ''), queryFn: () => get<EstadoCuentaDTO>(`/clientes/${id}/estado-cuenta`), enabled: !!id });

function useInvalidar() {
  const qc = useQueryClient();
  return (id?: string) => Promise.all([qc.invalidateQueries({ queryKey: ['clientes', 'lista'] }), id ? qc.invalidateQueries({ queryKey: clavesClientes.estado(id) }) : Promise.resolve()]);
}
export function useCrearCliente() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: (d: ClienteCrearInput) => post<ClienteDTO>('/clientes', d), onSuccess: () => inv() });
}
export function useEditarClienteBasico(id: string) {
  const inv = useInvalidar();
  return useMutation({ mutationFn: (d: ClienteEditarBasicoInput) => patch<ClienteDTO>(`/clientes/${id}`, d), onSuccess: () => inv(id) });
}
/** Notas de crédito (y Cambios) del cliente con lo que todavía se puede traspasar de cada una (spec 03, enmienda 2026-10-29). */
export const useNotasConSaldo = (id: string, habilitado: boolean) => useQuery({ queryKey: ['clientes', 'notas-saldo', id], queryFn: () => get<NotaCreditoSaldoDTO[]>(`/clientes/${id}/notas-credito-saldo`), enabled: habilitado && !!id });
/** Traspaso y ajuste de saldo: cambian el saldo de uno o dos clientes, así que se relee toda la lista y las fichas. */
function useInvalidarSaldo() {
  const qc = useQueryClient();
  return () => Promise.all([qc.invalidateQueries({ queryKey: ['clientes'] }), qc.invalidateQueries({ queryKey: ['ventas'] })]);
}
export function useTraspasarSaldo(id: string) {
  const inv = useInvalidarSaldo();
  return useMutation({ mutationFn: ({ datos, clave }: { datos: TraspasoSaldoInput; clave: string }) => api<OperacionSaldoDTO>('POST', `/clientes/${id}/traspasar-saldo`, { body: datos, headers: { 'idempotency-key': clave } }), onSuccess: () => void inv() });
}
export function useAjustarSaldo(id: string) {
  const inv = useInvalidarSaldo();
  return useMutation({ mutationFn: ({ datos, clave }: { datos: AjusteSaldoInput; clave: string }) => api<OperacionSaldoDTO>('POST', `/clientes/${id}/ajustar-saldo`, { body: datos, headers: { 'idempotency-key': clave } }), onSuccess: () => void inv() });
}
export function useEditarClienteCredito(id: string) {
  const inv = useInvalidar();
  return useMutation({ mutationFn: (d: ClienteCreditoInput) => patch<ClienteDTO>(`/clientes/${id}/credito`, d), onSuccess: () => inv(id) });
}
